import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { getCookie, setCookie, removeCookie } from "../helpers/cookies";
import {
  AUTH_URL,
  BASE_URL,
  GLOBAL_BASE_URL,
  BACK_OFFICE_URL,
  METER_CMD_URL,
  PAYMENT_URL,
  SCHEDULER_URL,
  BILLING_URL,
  GUEST_MGT_URL,
} from "../global/constants";
import { fetchGates, getClientByNetwork } from "../api/clients-api";

export const AuthContext = createContext();

// Axios instances
export const axi = axios.create({ baseURL: BASE_URL });
export const global_axi = axios.create({ baseURL: GLOBAL_BASE_URL });
export const back_office_axi = axios.create({ baseURL: BACK_OFFICE_URL });
export const meter_cmd_axi = axios.create({ baseURL: METER_CMD_URL });
export const payment_axi = axios.create({ baseURL: PAYMENT_URL });
export const scheduler_axi = axios.create({ baseURL: SCHEDULER_URL });
export const auth_url = axios.create({ baseURL: AUTH_URL });
export const billing_axi = axios.create({ baseURL: BILLING_URL });
export const guest_mgt_axi = axios.create({ baseURL: GUEST_MGT_URL });

// Apply request interceptor to attach token
const axiosInstances = [
  axi,
  global_axi,
  meter_cmd_axi,
  back_office_axi,
  payment_axi,
  scheduler_axi,
  auth_url,
  billing_axi,
  guest_mgt_axi,
];
axiosInstances.forEach((instance) => {
  instance.interceptors.request.use(
    (config) => {
      const token = getCookie("token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getCookie("token"));
  const [role, setRole] = useState(null);
  const [client, setClient] = useState(null);
  // const [dataLoading, setDataLoading] = useState(false);
  const [licensed, setLicensed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [profileChecked, setProfileChecked] = useState(false);

  const logout = async () => {
    const refresh_token = getCookie("refresh_token");

    setToken(null);
    setUser(null);
    setRole(null);
    setClient(null);
    setProfileChecked(false);

    removeCookie("token");
    removeCookie("user");
    removeCookie("role");
    removeCookie("refresh_token");

    // Fire and forget — don't block state clearing on this
    auth_url.post("/passport/logout", { refresh_token }).catch(() => {});
  };

  useEffect(() => {
    const savedToken = getCookie("token");
    const savedUser = getCookie("user");
    const savedRole = getCookie("role");
    const savedRefreshToken = getCookie("refresh_token");

    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      setRole(savedRole);
      setAuthLoading(false);
    } else if (!savedToken && savedRefreshToken && savedUser) {
      // Token missing but refresh token exists — silently get a new token
      auth_url
        .post("/passport/refresh-login", { refresh_token: savedRefreshToken })
        .then(({ data }) => {
          const newToken = data.token;
          setCookie("token", newToken);
          setToken(newToken);
        })
        .catch(() => {
          logout();
        })
        .finally(() => setAuthLoading(false));
    } else {
      setAuthLoading(false);
    }
  }, []);

  // Attach response interceptor to handle 401 errors
  useEffect(() => {
    const ejectors = axiosInstances.map((instance) =>
      instance.interceptors.response.use(
        (res) => res,
        async (error) => {
          if (
            error.response?.status === 401 &&
            !error.config._retry &&
            !error.config.url?.includes("refresh-login")
          ) {
            error.config._retry = true;
            const refresh_token = getCookie("refresh_token");
            try {
              const { data } = await auth_url.post("/passport/refresh-login", {
                refresh_token,
              });
              const newToken = data.token;
              setToken(newToken);
              setCookie("token", newToken);
              error.config.headers["Authorization"] = `Bearer ${newToken}`;
              return instance(error.config);
            } catch (refreshError) {
              console.error("Token refresh failed:", refreshError);
              logout();
            }
          }
          return Promise.reject(error);
        },
      ),
    );

    return () => {
      axiosInstances.forEach((instance, index) => {
        instance.interceptors.response.eject(ejectors[index]);
      });
    };
  }, []);

  useEffect(() => {
    if (token) {
      setCookie("token", token);
    } else {
      removeCookie("token");
    }
  }, [token]);

  const normalizeClientProfile = (response) =>
    response?.data?.data || response?.data?.client || response?.data || null;

  const fetchProfileData = async (profileUser) => {
    const network = (profileUser?.network || "").trim().toLowerCase();
    if (!network || network === "unassigned") {
      setClient(null);
      setProfileChecked(true);
      return;
    }

    try {
      setLoading(true);
      setProfileChecked(false);
      const response = await getClientByNetwork(network);
      const profile = normalizeClientProfile(response);
      setClient(profile);

      if (profile?.network) {
        setUser((prevUser) =>
          prevUser ? { ...prevUser, network: profile.network } : prevUser,
        );
        setCookie("network", profile.network.trim());
      }
    } catch (error) {
      if (error?.response?.status === 404) {
        setClient(null);
      } else {
        console.error("Error fetching profile data:", error);
      }
    } finally {
      setLoading(false);
      setProfileChecked(true);
    }
  };

  useEffect(() => {
    if (user) fetchProfileData(user);
  }, [user?.network]);

  const fetchGateData = async (clientId) => {
    try {
      const response = await fetchGates(clientId);
      setLicensed(response?.data.has_valid_license || false);
    } catch (error) {
      console.error("Error fetching license data:", error);
    }
  };

  useEffect(() => {
    if (client?.client_id) fetchGateData(client.client_id);
  }, [client?.client_id]);

  const login = async ({ email, password }) => {
    setLoading(true);
    try {
      const normalizedEmail = (email || "").trim().toLowerCase();
      const response = await auth_url.post("/passport/login", {
        email: normalizedEmail,
        password,
      });
      const { token: accessToken, user, refresh_token } = response.data;

      if (user.role_name !== "landlord") {
        logout();
        throw { message: "You don't have access here" };
      }

      setToken(accessToken);
      setUser(user);
      setRole(user.role_name?.toLowerCase() || null);

      setCookie("token", accessToken);
      setCookie(
        "user",
        JSON.stringify({
          name: user.name,
          email: user.email,
          network: user.network?.trim(),
        }),
      );
      setCookie("role", user.role_name?.toLowerCase() || "");
      setCookie("network", user.network?.trim());
      if (refresh_token) {
        setCookie("refresh_token", refresh_token, { expires: 180 });
      }

      return response.data;
    } catch (err) {
      setRole(null);
      const message =
        err?.message ||
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "An Error Occurred while logging in";
      throw { message };
    } finally {
      setLoading(false);
    }
  };

  const applyAuthSession = ({ token: accessToken, user, refresh_token }) => {
    if (!accessToken || !user) {
      throw { message: "Unable to start authenticated session" };
    }

    const normalizedRole = user.role_name?.toLowerCase() || "";
    const normalizedUser = {
      name: user.name,
      email: user.email,
      network: user.network?.trim(),
    };

    setToken(accessToken);
    setUser(user);
    setRole(normalizedRole || null);

    setCookie("token", accessToken);
    setCookie("user", JSON.stringify(normalizedUser));
    setCookie("role", normalizedRole);
    setCookie("network", user.network?.trim());
    if (refresh_token) {
      setCookie("refresh_token", refresh_token, { expires: 180 });
    }
  };


  const createUser = async (userData) => {
    try {
      const response = await auth_url.post("/passport/create-user", userData);
      return response.data;
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "An Error Occurred while creating user";
      throw { message };
    }
  };

  const requestResetPassword = async (data) => {
    console.log("Requesting password reset for:", data);
    try {
      const response = await auth_url.post(
        "/passport/request-reset-password",
        data,
      );
      return response.data;
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "An error occurred while requesting password reset";
      throw { message };
    }
  };

  const resetPassword = async (data) => {
    try {
      const response = await auth_url.post("/passport/reset-password", data);
      return response.data;
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "An error occurred while resetting password";
      throw { message };
    }
  };

  const sendEmailConfirmation = async (data) => {
    try {
      const response = await auth_url.post(
        "/passport/send-confirmation-email",
        data,
      );
      return response.data;
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "An error occurred while sending email confirmation";
      throw { message };
    }
  };

  const confirmEmail = async (data) => {
    try {
      const response = await auth_url.post("/passport/confirm-email", data);
      return response.data;
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "An error occurred while confirming email";
      throw { message };
    }
  };

  const hasRole = (targetRole) => role === targetRole.toLowerCase();
  const activeNetwork = (client?.network || user?.network || "")
    .trim()
    .toLowerCase();

  return (
    <AuthContext.Provider
      value={{
        user,
        client,
        setClient,
        licensed,
        token,
        role,
        NETWORK: activeNetwork,
        hasRole,
        createUser,
        applyAuthSession,
        setUser,
        login,
        logout,
        requestResetPassword,
        resetPassword,
        sendEmailConfirmation,
        confirmEmail,
        auth_url,
        loading,
        authLoading,
        profileChecked,
        setProfileChecked,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
